import React from 'react';
import { useNavigate } from 'react-router-dom';

import type { EventType } from '../../api/types';
import {
  applyDashboardFilters,
  defaultDashboardFilters,
  getPeriodDateRange,
  groupAllEventsByDayAndType,
  type DashboardFilters,
} from '../../domain/events/dashboard';
import { canCreateEvent } from '../../domain/events/policy';
import { EventCard } from '../../features/events/EventCard';
import { useMatchingEventViews } from '../../features/matching/useMatchingEventViews';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchEventsRange } from '../../store/thunks';
import {
  TavernButton,
  TavernDayHeader,
  TavernLoading,
  TavernEmptyState,
} from '../../ui/components';
import { formatDateYYYYMMDD, parseDateYYYYMMDD } from '../../utils/date';
import { logger } from '../../utils/logger';

import { DashboardFiltersPanel } from './DashboardFilters';
import './EventsDashboardPage.css';

const TYPE_LABEL: Record<EventType, string> = {
  boardgames: '🎲 Настольные игры',
  ping_pong: '🏓 Настольный теннис',
  fencing: '⚔️ Фехтование',
  archery: '🏹 Лук',
  hiking: '🌲 Походы на природу',
  courtyard: '🏡 Двор',
};

/**
 * Events Dashboard Page - Tavern Theme
 * Quest bulletin board showing all adventures
 */
export function EventsDashboardPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const events = useAppSelector((s) => s.entities.events.items);
  useMatchingEventViews(React.useMemo(() => events.map((e) => e.id), [events]));
  const role = useAppSelector((s) => s.auth.user?.role);
  const currentUserTelegramId = useAppSelector((s) => s.auth.user?.telegramId ?? '');
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [filters, setFilters] = React.useState<DashboardFilters>(defaultDashboardFilters);

  const canCreate = canCreateEvent(role);
  const showCreatorFilter = role === 'organizer' || role === 'admin';

  const [collapsedDays, setCollapsedDays] = React.useState<Set<string>>(new Set());

  logger.info('EventsDashboardPage->', 'events', events);

  const toggleDay = (dayKey: string) => {
    setCollapsedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayKey)) {
        next.delete(dayKey);
      } else {
        next.add(dayKey);
      }
      return next;
    });
  };

  const { from, to } = getPeriodDateRange(filters.period);

  const load = React.useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      await dispatch(fetchEventsRange({ from, to })).unwrap();
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, from, to]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const filteredEvents = React.useMemo(() => {
    return applyDashboardFilters({ events, filters, currentUserTelegramId });
  }, [events, filters, currentUserTelegramId]);

  // Also apply date range filter to stay consistent with fetched data
  const eventsInRange = React.useMemo(() => {
    return filteredEvents.filter((e) => {
      const dayKey = e.startAt.slice(0, 10);
      return dayKey >= from && dayKey <= to;
    });
  }, [filteredEvents, from, to]);

  const grouped = React.useMemo(() => {
    return groupAllEventsByDayAndType(eventsInRange);
  }, [eventsInRange]);

  const today = formatDateYYYYMMDD(new Date());

  logger.info('EventsDashboardPage->', 'filteredEvents', filteredEvents);
  logger.info('EventsDashboardPage->', 'grouped', grouped);

  return (
    <div className="events-dashboard">
      <div className="events-dashboard-header">
        <h1 className="events-dashboard-title">
          <span>📜</span>
          Приключения
        </h1>
      </div>

      <DashboardFiltersPanel
        filters={filters}
        onChange={setFilters}
        showCreatorFilter={showCreatorFilter}
      />

      <div className="events-content">
        {isLoading ? (
          <TavernLoading message="Загружаем квесты..." />
        ) : hasError ? (
          <TavernEmptyState
            icon="⚠️"
            title="Не удалось загрузить мероприятия"
            description="Проверьте подключение и попробуйте снова"
            action={
              <TavernButton variant="secondary" onClick={load}>
                Повторить
              </TavernButton>
            }
          />
        ) : grouped.length === 0 ? (
          <TavernEmptyState
            icon="🗺️"
            title="Нет квестов по выбранным фильтрам"
            description={
              canCreate
                ? 'Создайте первое приключение для путешественников'
                : 'Квесты появятся, когда их создадут организаторы'
            }
            action={
              canCreate ? (
                <TavernButton
                  variant="secondary"
                  onClick={() => navigate('/events/new')}
                >
                  Создать квест
                </TavernButton>
              ) : undefined
            }
          />
        ) : (
          <div className="ttbStackLg">
            {grouped.map((day) => {
              const isToday = day.dayKey === today;
              const dayDate = parseDateYYYYMMDD(day.dayKey) ?? new Date(day.dayKey);
              const dayName = dayDate.toLocaleDateString('ru-RU', { weekday: 'long' });
              const isDayCollapsed = collapsedDays.has(day.dayKey);
              const WEEKDAY_SHORT: Record<number, string> = {
                0: 'Вс.', 1: 'Пон.', 2: 'Вт.', 3: 'Ср.', 4: 'Чт.', 5: 'Пт.', 6: 'Сб.',
              };
              const dd = String(dayDate.getDate()).padStart(2, '0');
              const mm = String(dayDate.getMonth() + 1).padStart(2, '0');
              const dayLabel = WEEKDAY_SHORT[dayDate.getDay()] ?? dayName;
              const dateShort = `${dd}.${mm}`;
              const daySectionId = `day-content-${day.dayKey}`;

              return (
                <section key={day.dayKey} className="ttbStackMd">
                  <button
                    type="button"
                    className="day-header-wrapper"
                    onClick={() => toggleDay(day.dayKey)}
                    aria-expanded={!isDayCollapsed}
                    aria-controls={daySectionId}
                  >
                    <TavernDayHeader
                      day={dayLabel}
                      date={dateShort}
                      isToday={isToday}
                      uppercase={false}
                    />
                    <div className={`day-header-arrow ${isDayCollapsed ? 'day-header-arrow--collapsed' : ''}`}>
                      ▾
                    </div>
                  </button>
                  <div
                    id={daySectionId}
                    className={`day-content ${isDayCollapsed ? 'collapsed' : ''}`}
                  >
                    {day.items.map(([type, list]) => (
                      <div key={type} className="type-section">
                        <div className="type-subheader">
                          {TYPE_LABEL[type]}
                        </div>
                        <div className="type-content">
                          {list.map((e) => (
                            <EventCard key={e.id} event={e} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
